import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Col, Form, Offcanvas, Row } from 'react-bootstrap';
import { Navigate, useLocation } from 'react-router-dom';

import DataTable, { Column } from '../../components/DataTable';
import ProjectAreaLayout from '../../components/projects/ProjectAreaLayout';
import ProjectEmptyState from '../../components/projects/ProjectEmptyState';
import ProjectLoadingState from '../../components/projects/ProjectLoadingState';
import ProjectPageHeader from '../../components/projects/ProjectPageHeader';
import ProjectSectionCard from '../../components/projects/ProjectSectionCard';
import ProjectsAlertStack from '../../components/projects/ProjectsAlertStack';
import ProjectsKpiCardPicker from '../../components/projects/ProjectsKpiCardPicker';
import ProjectsKpiMetricGrid from '../../components/projects/ProjectsKpiMetricGrid';
import MonthYearInput from '../../components/projects/MonthYearInput';
import { useArea } from '../../context/AreaContext';
import { useAuth } from '../../context/AuthContext';
import { useModal } from '../../context/ModalContext';
import { API_URL } from '../../config';
import { ComprasEExportHoursResponse, ComprasEExportIssueRow } from '../../types';
import { buildFriendlyApiError } from '../../utils/apiError';
import { pmoPeriodToWorklogIsoDates } from './pmoWorklogPeriodBounds';
import '../ProjectsDashboard.css';
import {
  COMPRAS_E_ALL_ISSUE_TYPES,
  COMPRAS_E_DEFAULT_PERIOD_START,
  COMPRAS_E_PACK_OPTIONS,
  ComprasEPackFilter,
  comprasEIssueTypeOptions,
  currentMonthYear,
  fetchComprasEExportHoursPanel,
  filterComprasEExportIssues,
  filterComprasEIssuesByType,
  formatComprasECurrency,
  formatComprasEHours,
  formatComprasEPercent,
  comprasEEsteiraBadgeLabel,
  parentJiraBrowseUrl,
  shouldFetchComprasEExportHours,
  soldHoursForComprasEPack,
  shouldShowComprasESoldKpis,
  summarizeComprasEExportIssues,
} from './comprasEExportPanel';
import {
  applyComprasEKpiConfigs,
  COMPRAS_E_KPI_OPTIONS,
  loadComprasEKpiConfigs,
  replaceLegacySubtaskWithParent,
  appendMissingCatalogKpis,
  saveComprasEKpiConfigs,
} from './comprasEKpiConfig';
import {
  loadComprasETableColumns,
  resolveComprasETableSortColumn,
  saveComprasETableColumns,
  visibleComprasETableColumns,
} from './comprasETableColumns';
import { PmoKpiConfig, reorderKpiConfigs } from './pmoKpiConfig';
import ComprasEColumnPicker from './ComprasEColumnPicker';
import ComprasERegisterUnmatchedCollaboratorModal from './ComprasERegisterUnmatchedCollaboratorModal';

const formatUpdatedAt = (value: number | null): string => {
  if (!value) return '';
  return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const ProjectsComprasEPage: React.FC = () => {
  const { user } = useAuth();
  const { setArea } = useArea();
  const { showAlert } = useModal();
  const location = useLocation();
  const canAccess = user?.role === 'admin' || Boolean(user?.can_access_projects);
  const canWriteCosts = user?.role === 'admin' || user?.role === 'analista';

  const [periodStart, setPeriodStart] = useState(COMPRAS_E_DEFAULT_PERIOD_START);
  const [periodEnd, setPeriodEnd] = useState(() => currentMonthYear());
  const [pack, setPack] = useState<ComprasEPackFilter>('all');
  const [search, setSearch] = useState('');
  const [issueType, setIssueType] = useState(COMPRAS_E_ALL_ISSUE_TYPES);
  const [payload, setPayload] = useState<ComprasEExportHoursResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortColumn, setSortColumn] = useState('hours_in_period');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [kpiConfigs, setKpiConfigs] = useState<PmoKpiConfig[]>(
    () => replaceLegacySubtaskWithParent(loadComprasEKpiConfigs()),
  );
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    () => loadComprasETableColumns(),
  );
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [registerUnmatchedOpen, setRegisterUnmatchedOpen] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!canAccess || !location.pathname.startsWith('/projects')) return;
    setArea('projects');
  }, [canAccess, location.pathname, setArea]);

  const loadPanel = useCallback(async () => {
    if (!shouldFetchComprasEExportHours(periodStart, periodEnd)) {
      return;
    }
    const iso = pmoPeriodToWorklogIsoDates(periodStart, periodEnd);
    if (!iso.worklogStartDate || !iso.worklogEndDate) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const data = await fetchComprasEExportHoursPanel(
        API_URL,
        iso.worklogStartDate,
        iso.worklogEndDate,
      );
      if (requestId !== requestIdRef.current) return;
      setPayload(data);
      setUpdatedAt(Date.now());
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      const friendly = buildFriendlyApiError(err, {
        defaultMessage: 'Não foi possível recalcular o recorte Compras-e no Jira.',
      });
      setError([friendly.message, friendly.details].filter(Boolean).join(' '));
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [periodEnd, periodStart]);

  useEffect(() => {
    void loadPanel();
  }, [loadPanel]);

  useEffect(() => {
    const visible = replaceLegacySubtaskWithParent(kpiConfigs);
    if (JSON.stringify(visible) !== JSON.stringify(kpiConfigs)) {
      setKpiConfigs(visible);
      return;
    }
    saveComprasEKpiConfigs(visible);
  }, [kpiConfigs]);

  useEffect(() => {
    saveComprasETableColumns(selectedColumns);
  }, [selectedColumns]);

  useEffect(() => {
    const nextSort = resolveComprasETableSortColumn(sortColumn, selectedColumns);
    if (nextSort !== sortColumn) {
      setSortColumn(nextSort);
    }
  }, [selectedColumns, sortColumn]);

  const filteredIssues = useMemo(
    () => filterComprasEExportIssues(payload?.issues || [], pack, search),
    [pack, payload?.issues, search],
  );

  const kpis = useMemo(() => {
    const soldHours = soldHoursForComprasEPack(pack, payload?.sold_hours);
    return summarizeComprasEExportIssues(filteredIssues, soldHours, {
      subtaskCount: payload?.subtask_count,
      subtaskNaoIniciadoCount: payload?.subtask_nao_iniciado_count,
      subtaskTaggedCount: payload?.subtask_tagged_count,
    });
  }, [filteredIssues, pack, payload]);

  const issueTypeOptions = useMemo(
    () => comprasEIssueTypeOptions(filteredIssues),
    [filteredIssues],
  );

  useEffect(() => {
    if (issueType === COMPRAS_E_ALL_ISSUE_TYPES) return;
    const stillAvailable = issueTypeOptions.some(
      (option) => option.toLowerCase() === issueType.toLowerCase(),
    );
    if (!stillAvailable) setIssueType(COMPRAS_E_ALL_ISSUE_TYPES);
  }, [issueType, issueTypeOptions]);

  const tableIssues = useMemo(
    () => filterComprasEIssuesByType(filteredIssues, issueType),
    [filteredIssues, issueType],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [pack, search, issueType, periodStart, periodEnd]);

  const sortedIssues = useMemo(() => {
    const rows = [...tableIssues];
    const direction = sortDirection === 'asc' ? 1 : -1;
    const textValue = (issue: ComprasEExportIssueRow, column: string): string => {
      if (column in issue) {
        return String(issue[column as keyof ComprasEExportIssueRow] ?? '').toLowerCase();
      }
      return '';
    };
    rows.sort((left, right) => {
      if (sortColumn === 'hours_in_period' || sortColumn === 'worklog_count') {
        const leftValue = Number(left[sortColumn] || 0);
        const rightValue = Number(right[sortColumn] || 0);
        return (leftValue - rightValue) * direction;
      }
      if (
        sortColumn === 'own_cost_cents'
        || sortColumn === 'third_party_cost_cents'
        || sortColumn === 'cost_cents'
      ) {
        const leftValue = Number(left[sortColumn] || 0);
        const rightValue = Number(right[sortColumn] || 0);
        return (leftValue - rightValue) * direction;
      }
      return textValue(left, sortColumn).localeCompare(textValue(right, sortColumn), 'pt-BR') * direction;
    });
    return rows;
  }, [tableIssues, sortColumn, sortDirection]);

  const pagedIssues = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedIssues.slice(start, start + itemsPerPage);
  }, [currentPage, itemsPerPage, sortedIssues]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === 'hours_in_period' || column === 'worklog_count' || column.endsWith('_cents') ? 'desc' : 'asc');
  };

  const kpiItems = applyComprasEKpiConfigs([
    {
      id: 'issue_count',
      label: 'Itens com apontamento',
      value: kpis.issueCount.toLocaleString('pt-BR'),
      help: 'Quantidade de itens que receberam pelo menos uma hora de trabalho dentro do período escolhido em De/Até. Cada item é contado uma única vez, mesmo que tenha vários lançamentos de hora. Itens sem nenhuma hora no período não entram nesta conta.',
    },
    {
      id: 'parent_count',
      label: 'Demandas com apontamento',
      value: kpis.parentCount.toLocaleString('pt-BR'),
      help: 'Quantidade de demandas principais que tiveram algum trabalho realizado no período. Mostra em quantas frentes houve esforço, e não a quantidade de horas — essas aparecem no HH Real (período).',
    },
    {
      id: 'subtask_count',
      label: 'Subtarefas Compras-e',
      value: kpis.subtaskCount.toLocaleString('pt-BR'),
      help: `Tarefas identificadas como Compras-e, sendo ${kpis.subtaskNaoIniciadoCount.toLocaleString('pt-BR')} ainda não iniciadas. Diferente dos demais cards, este não depende de horas lançadas: mostra o trabalho previsto, mesmo o que ainda não começou.`,
    },
    {
      id: 'portal_hours',
      label: 'HH Portal de Compras',
      value: formatComprasEHours(kpis.portalHours),
      help: 'Horas trabalhadas no período pela frente do Compras-e que corre dentro do Portal de Compras. Considera apenas as demandas do Portal identificadas como Compras-e e o trabalho realizado dentro delas; o restante do Portal não entra.',
    },
    {
      id: 'cmpe_hours',
      label: 'HH CMPE',
      value: formatComprasEHours(kpis.cmpeHours),
      help: 'Horas trabalhadas no período na frente própria do Compras-e, que corre separada do Portal de Compras. Junto com o HH Portal de Compras, forma o total de horas realizadas.',
    },
    {
      id: 'sap_hours',
      label: 'HH SAP',
      value: formatComprasEHours(kpis.sapHours),
      help: 'Parte das horas do Portal de Compras dedicada ao trabalho de SAP. Atenção: este valor já está incluído no HH Portal de Compras e no HH Real, portanto não deve ser somado a eles.',
    },
    {
      id: 'total_hours',
      label: 'HH Real (período)',
      value: formatComprasEHours(kpis.totalHours),
      help: 'Total de horas efetivamente trabalhadas no Compras-e dentro do período escolhido, somando as duas frentes: Portal de Compras e Compras-e. Considera apenas as horas lançadas entre as datas De e Até.',
    },
    {
      id: 'sold_hours',
      label: 'HH Vendido',
      value: shouldShowComprasESoldKpis(pack) && kpis.soldHours != null ? formatComprasEHours(kpis.soldHours) : '—',
      help: shouldShowComprasESoldKpis(pack)
        ? 'Total de horas contratadas das demandas que tiveram trabalho no período. Cada demanda entra uma única vez, independentemente de quantas pessoas ou tarefas trabalharam nela.'
        : 'Disponível apenas com Esteira em "Todas": as horas contratadas pertencem à demanda inteira e não podem ser divididas por frente de trabalho.',
    },
    {
      id: 'consumo',
      label: '% Consumo (período)',
      value: shouldShowComprasESoldKpis(pack) ? formatComprasEPercent(kpis.consumptionPercent) : '—',
      help: 'Quanto das horas contratadas já foi consumido pelo trabalho realizado. Acima de 100% indica que o esforço ultrapassou o que foi vendido. Prefira analisar períodos completos: em intervalos curtos o percentual fica menor, porque as horas contratadas são sempre as totais da demanda.',
    },
    {
      id: 'custo_total',
      label: 'Custo total (período)',
      value: formatComprasECurrency(kpis.totalCostCents),
      help: 'Valor das horas trabalhadas no período. Cada hora é multiplicada pelo valor/hora de quem a realizou, conforme o cadastro de Custos de Projetos. Acompanha os filtros de Esteira e Busca.',
    },
    {
      id: 'custo_proprio',
      label: 'Custo próprio',
      value: formatComprasECurrency(kpis.ownCostCents),
      help: 'Parte do custo referente a profissionais da própria empresa. Se quem lançou a hora ainda não estiver no cadastro de Custos de Projetos, a hora continua contando nos totais de horas, mas fica sem valor e reduz o custo exibido.',
    },
    {
      id: 'custo_terceiro',
      label: 'Custo terceiro',
      value: formatComprasECurrency(kpis.thirdPartyCostCents),
      help: 'Parte do custo referente a profissionais terceirizados. Somada ao custo próprio, resulta no custo total do período.',
    },
  ], appendMissingCatalogKpis(replaceLegacySubtaskWithParent(kpiConfigs)));

  const columns = useMemo<Column<ComprasEExportIssueRow & { id: string }>[]>(() => [
    {
      key: 'key',
      label: 'Chave',
      sortable: true,
      render: (item) => (
        item.jira_browse_url ? (
          <a href={item.jira_browse_url} target="_blank" rel="noopener noreferrer" className="projects-project-link">
            {item.key}
          </a>
        ) : item.key
      ),
    },
    { key: 'summary', label: 'Resumo', sortable: true },
    {
      key: 'parent_key',
      label: 'Pai',
      sortable: true,
      render: (item) => {
        const parentKey = String(item.parent_key || '').trim();
        if (!parentKey) return '—';
        const parentUrl = parentJiraBrowseUrl(item);
        const parentLabel = item.parent_summary
          ? `${parentKey} — ${item.parent_summary}`
          : parentKey;
        const content = (
          <span title={parentLabel}>{parentKey}</span>
        );
        if (!parentUrl) return content;
        return (
          <a href={parentUrl} target="_blank" rel="noopener noreferrer" className="projects-project-link" title={parentLabel}>
            {parentKey}
          </a>
        );
      },
    },
    {
      key: 'esteira',
      label: 'Esteira',
      sortable: true,
      render: (item) => (
        <Badge bg={item.esteira === 'cmpe' || item.project_key === 'CMPE' ? 'secondary' : 'info'}>
          {comprasEEsteiraBadgeLabel(item)}
        </Badge>
      ),
    },
    {
      key: 'pack',
      label: 'SAP',
      sortable: true,
      render: (item) => (
        item.pack === 'sap' ? <Badge bg="primary">SAP</Badge> : '—'
      ),
    },
    { key: 'issue_type', label: 'Tipo', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'project_key', label: 'Projeto', sortable: true },
    {
      key: 'hours_in_period',
      label: 'HH no período',
      sortable: true,
      render: (item) => formatComprasEHours(Number(item.hours_in_period || 0)),
    },
    {
      key: 'own_cost_cents',
      label: 'Custo próprio',
      sortable: true,
      render: (item) => formatComprasECurrency(Number(item.own_cost_cents || 0)),
    },
    {
      key: 'third_party_cost_cents',
      label: 'Custo terceiro',
      sortable: true,
      render: (item) => formatComprasECurrency(Number(item.third_party_cost_cents || 0)),
    },
    {
      key: 'cost_cents',
      label: 'Custo total',
      sortable: true,
      render: (item) => formatComprasECurrency(Number(item.cost_cents || 0)),
    },
    {
      key: 'worklog_count',
      label: 'Worklogs',
      sortable: true,
    },
  ], []);

  const tableColumns = useMemo(
    () => visibleComprasETableColumns(columns, selectedColumns),
    [columns, selectedColumns],
  );

  const tableRows = pagedIssues.map((issue) => ({ ...issue, id: issue.key }));
  const unmatchedAuthors = payload?.unmatched_authors || [];
  const warning = [
    payload?.incomplete ? 'A busca no Jira veio incompleta; o total pode estar parcial.' : '',
    ...(payload?.warnings || []),
  ].filter(Boolean).join(' ');

  if (!canAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <ProjectAreaLayout className="projects-dashboard-page">
      <ProjectPageHeader
        title="Compras-e"
        subtitle="Horas e custos do Compras-e nas duas frentes de trabalho: as demandas do Compras-e dentro do Portal de Compras e a frente própria do Compras-e. Demandas novas entram automaticamente, e horas lançadas agora no Jira aparecem ao clicar em Atualizar."
        contextBadge="Relatório Compras-e"
        contextLabel={updatedAt ? `Atualizado às ${formatUpdatedAt(updatedAt)}` : 'Ao vivo no Jira'}
        actions={[
          {
            id: 'customize-view',
            label: 'Personalizar',
            variant: 'outline-secondary',
            onClick: () => setCustomizeOpen(true),
          },
          {
            id: 'refresh',
            label: loading ? 'Atualizando…' : 'Atualizar',
            onClick: () => { void loadPanel(); },
            disabled: loading || !shouldFetchComprasEExportHours(periodStart, periodEnd),
          },
        ]}
      />
      <ProjectsAlertStack
        error={error}
        warning={warning}
        warningAction={unmatchedAuthors.length && canWriteCosts ? (
          <Button
            size="sm"
            variant="outline-warning"
            className="mt-2"
            onClick={() => setRegisterUnmatchedOpen(true)}
          >
            Cadastrar colaborador
          </Button>
        ) : undefined}
      />
      <div className="projects-dashboard-main-flow">
        <ProjectSectionCard
          title="Filtros"
          subtitle="De/Até consideram a data em que a hora foi lançada. Esteira e busca filtram a tabela e os cards ao mesmo tempo."
        >
          <Row className="g-3 align-items-start">
            <Col md={3}>
              <MonthYearInput
                label="De"
                value={periodStart}
                onChange={setPeriodStart}
                disabled={loading}
              />
            </Col>
            <Col md={3}>
              <MonthYearInput
                label="Até"
                value={periodEnd}
                onChange={setPeriodEnd}
                disabled={loading}
              />
            </Col>
            <Col md={3}>
              <Form.Group controlId="compras-e-esteira">
                <Form.Label className="small mb-1">Esteira</Form.Label>
                <Form.Select
                  size="sm"
                  value={pack}
                  disabled={loading}
                  onChange={(event) => setPack(event.target.value as ComprasEPackFilter)}
                >
                  {COMPRAS_E_PACK_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="compras-e-busca">
                <Form.Label className="small mb-1">Busca</Form.Label>
                <Form.Control
                  size="sm"
                  value={search}
                  disabled={loading}
                  placeholder="Chave, resumo ou pai"
                  onChange={(event) => setSearch(event.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </ProjectSectionCard>

        {kpiItems.length ? (
          <ProjectsKpiMetricGrid
            items={kpiItems}
            onReorder={(sourceId, targetId) => {
              setKpiConfigs((current) => reorderKpiConfigs(current, sourceId, targetId));
            }}
          />
        ) : (
          <ProjectEmptyState
            title="Nenhum KPI visível"
            description="Abra Personalizar para marcar os cards que devem aparecer acima da tabela."
            actionLabel="Personalizar"
            onAction={() => setCustomizeOpen(true)}
          />
        )}

        <ProjectSectionCard
          title="Itens do recorte"
          subtitle={`${tableIssues.length.toLocaleString('pt-BR')} itens com apontamento no período`}
        >
          {loading && !payload ? (
            <ProjectLoadingState message="Consultando as horas lançadas no Jira…" />
          ) : !filteredIssues.length ? (
            <ProjectEmptyState
              title="Nenhum apontamento neste recorte"
              description="Nenhuma hora foi lançada no Compras-e com os filtros atuais. Ajuste o período em De/Até ou a esteira e tente de novo."
              actionLabel="Atualizar"
              onAction={() => { void loadPanel(); }}
            />
          ) : (
            <>
              <div className="d-flex justify-content-end mb-3">
                <Form.Group controlId="compras-e-tipo" style={{ minWidth: 240 }}>
                  <Form.Label className="small mb-1">Tipo da demanda</Form.Label>
                  <Form.Select
                    size="sm"
                    value={issueType}
                    disabled={loading}
                    onChange={(event) => setIssueType(event.target.value)}
                  >
                    <option value={COMPRAS_E_ALL_ISSUE_TYPES}>Todos os tipos</option>
                    {issueTypeOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <DataTable
                data={tableRows}
                columns={tableColumns}
                loading={loading}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                totalItems={sortedIssues.length}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(size) => {
                  setItemsPerPage(size);
                  setCurrentPage(1);
                }}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                emptyMessage="Nenhum item deste tipo no recorte atual"
                tableClassName="projects-issues-table"
              />
            </>
          )}
        </ProjectSectionCard>
      </div>

      <Offcanvas
        show={customizeOpen}
        onHide={() => setCustomizeOpen(false)}
        placement="end"
        className="projects-kpi-config-offcanvas"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Personalizar KPIs e colunas</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <ComprasEColumnPicker
            selected={selectedColumns}
            onChange={setSelectedColumns}
          />
          <div className="fw-semibold small mb-1">Cards KPI</div>
          <p className="text-muted small mb-3">
            Desmarque para omitir. Arraste ⋮⋮ para mudar a ordem. O campo abaixo do nome é o título do card.
          </p>
          <ProjectsKpiCardPicker
            options={COMPRAS_E_KPI_OPTIONS}
            value={kpiConfigs}
            onChange={setKpiConfigs}
            disabled={loading}
          />
        </Offcanvas.Body>
      </Offcanvas>
      <ComprasERegisterUnmatchedCollaboratorModal
        show={registerUnmatchedOpen}
        authors={unmatchedAuthors}
        onHide={() => setRegisterUnmatchedOpen(false)}
        onRegistered={loadPanel}
        showAlert={showAlert}
      />
    </ProjectAreaLayout>
  );
};

export default ProjectsComprasEPage;
